import { useLingui } from "@lingui/react/macro";
import type { BillingDepleted } from "@orbin/contracts";
import { useState } from "react";
import { depletedLimit, dimensionLabel, formatMeterUnits } from "../lib/metering";
import { rpc } from "../lib/rpc";
import { BrandMoment } from "./BrandMoment";
import { RechargeDialog } from "./RechargeDialog";

/** What a refused action shows when a dimension ran out: what ran out, and a way to add more. */
export function RechargePrompt({
  depleted,
  onPaid,
}: {
  depleted: BillingDepleted;
  onPaid?: () => void;
}) {
  const { t, i18n } = useLingui();
  const [open, setOpen] = useState(false);
  const [moving, setMoving] = useState(false);
  const local = depleted.localComputer;
  const label = dimensionLabel(depleted.dimension);
  const limit = depletedLimit(depleted);
  const used = formatMeterUnits(depleted.unit, depleted.usedUnits, i18n.locale || "en");
  const active = limit?.active ?? 0;
  const max = limit?.max ?? 0;
  // More credit does not free a cloud computer that the plan caps, so a busy computer has no
  // recharge to offer.
  const canRecharge = !limit && depleted.recharge.available && depleted.recharge.methods.length > 0;
  return (
    <span
      data-testid="recharge-prompt"
      className="inline-flex flex-wrap items-center gap-x-2 gap-y-1"
    >
      <BrandMoment kind="depleted" size={40} />
      <span>
        {limit?.kind === "cloud_capacity"
          ? t`Cloud computers are all busy right now. Try again shortly.`
          : limit
            ? max === 1
              ? t`Your cloud computer is already in use.`
              : t`Your cloud computers are all in use (${active} of ${max}).`
            : t`${label} ran out (${used} used).`}
      </span>
      {limit?.kind === "concurrent_cloud_computers" ? (
        <a
          href="?settings=billing"
          data-testid="upgrade-link"
          className="font-medium underline underline-offset-2 hover:text-foreground"
        >
          {t`Change plan`}
        </a>
      ) : null}
      {canRecharge ? (
        <>
          <button
            type="button"
            data-testid="recharge-open"
            onClick={() => setOpen(true)}
            className="font-medium underline underline-offset-2 hover:text-foreground"
          >
            {t`Add credit`}
          </button>
          <RechargeDialog open={open} onOpenChange={setOpen} onPaid={onPaid} />
        </>
      ) : null}
      {local?.botId ? (
        <button
          type="button"
          data-testid="use-my-computer"
          disabled={moving}
          onClick={() => {
            setMoving(true);
            void rpc.bots
              .setComputer({ botId: local.botId!, mode: "local" })
              .then(() => onPaid?.())
              .catch(() => undefined)
              .finally(() => setMoving(false));
          }}
          className="font-medium underline underline-offset-2 hover:text-foreground"
        >
          {local.name ? t`Use my computer (${local.name})` : t`Use my computer`}
        </button>
      ) : null}
    </span>
  );
}
